import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Chip,
  Tabs,
  Tab,
  Grid,
  Button,
} from "@mui/material";
import { GridExpandMoreIcon } from "@mui/x-data-grid";
import React, { useState, useEffect } from "react";
import MismatchedRecordsDashboardTab from "./MismatchedRecordsDashboardTab";
import RootCauseAnalysis from "./RootCauseAnalysis";
import MissingRecordsDashboardTab from "./MissingRecordsDashboardTab";
import AdhocQueryDashboardTab from "./AdhocQueryDashboardTab";
import { useNavigate } from "react-router-dom";
import {
  IconBoltType,
  IconCheckCircleIconGreenSvg,
  IconCrossCircleIconSvg,
  IconFalsePositive,
  IconSecondaryResourceSvg,
} from "../../common/utils/icons";
import { useAuth } from "../../contexts/AuthContext";
import { useRowsPerPage } from "../../hooks/useRowsPerPage";
import {
  calculateDynamicWidthForSnapshot,
  getSanatizedCode,
  createColumnGroupingModel,
  getFieldToPrefixMappingForSnapshot,
  transformHeaderName,
  RecordType,
  createColumnGroupingModelForSnapshot,
  deduplicateByFieldProperty,
  excludeInternalResultColumns,
  getSecondaryMergeResourceName,
  getSecondaryMergeResources,
  SECONDARY_MERGE_RESOURCES_KEY,
} from "../../services/utils";
import { processIssueAnalysisData } from "../../common/utils/commonFunctions";
import { Tooltip, Typography } from "@mui/material";
import useFetchMissingRecordsWithSnapshotsByExecutionId from "../../hooks/useFetchMissingRecordsWithSnapshotsByExecutionId";
import MissingSnapshotRecordsDashboardTab from "./MissingSnapshotRecordsDashboardTab";
import MismatchedSnapshotRecordsDashboardTab from "./MismatchedSnapshotRecordsDashboardTab";
import { getConfig } from "../../config/runtimeConfig";
import { resolveAdhocQueryRecordCount } from "./adhocQueryCount";

const ComparisonDashboardResults = ({
  dashboardData,
  isLoading,
  setIsLoading,
}: any) => {
  const navigate = useNavigate();
  const { hasPermission } = useAuth();
  const canEditRuleExecutionIssues = hasPermission(
    "rule_execution_issues",
    "edit",
  );
  const [selectedTab, setSelectedTab] = useState<any>("missingRecords");
  const [missingColumns, setMissingColumns] = useState<any>([]);
  const [mismatchedColumns, setMismatchedColumns] = useState<any>([]);
  const [userDialogData, setUserDialogData] = useState<any>({
    status: false,
    dialogName: "",
    selectedIds: [],
  });

  // Missing records data processing states
  const [missingRowsData, setMissingRowsData] = useState<any>([]);
  const [missingColumnsData, setMissingColumnsData] = useState<any>([]);
  const [columnGroupingModel, setColumnGroupingModel] = useState<any>([]);
  const [keyColumns, setKeyColumns] = useState<any>([]);

  // Pagination states for missing records
  const defaultPage = getConfig("REACT_APP_DEFAULT_PAGE", "1");
  const { rowsPerPage } = useRowsPerPage();
  const [page, setPage] = useState<number>(
    defaultPage ? parseInt(defaultPage) : 1,
  );
  const [pSize, setPSize] = useState<number>(rowsPerPage);
  const [isSnapshotLoading, setSnapshotLoading] = useState<boolean>(false);

  // Fetch snapshot missing records data
  const [missingResultsRuleDashboardDataSnapshot] =
    useFetchMissingRecordsWithSnapshotsByExecutionId({
      setIsLoading: setSnapshotLoading,
      isMissingFetch: !dashboardData?.is_issues_data_available,
      currentRuleResultId: dashboardData?.id,
      page,
      pSize,
    });

  const getFormattedKeysPairs = (
    missing_in: any,
    false_positive_missings: any,
    resource_prefixes: any,
    type?: "missing",
  ) => {
    const pairs: any = {};
    if (!missing_in || !resource_prefixes) return {};

    const secondaryMergeResources = getSecondaryMergeResources(
      false_positive_missings,
    );

    resource_prefixes.forEach((prefix: string) => {
      if (getSecondaryMergeResourceName(secondaryMergeResources, prefix)) {
        pairs[prefix] = "secondaryfulfilled";
        return;
      }
      pairs[prefix] = missing_in.includes(prefix)
        ? false_positive_missings[prefix]
          ? "falsepositive"
          : "falsenegative"
        : "truepositive";
    });
    pairs[SECONDARY_MERGE_RESOURCES_KEY] = secondaryMergeResources;

    return pairs;
  };

  const processRows = (rows: any[]) => {
    if (!rows) return;
    return rows.map((row: any) => {
      const updatedRow = { ...row };

      Object.keys(updatedRow).forEach((key) => {
        if (typeof updatedRow[key] === "string") {
          if (
            /\.0$/.test(updatedRow[key]) &&
            !/\.0[1-9]/.test(updatedRow[key])
          ) {
            updatedRow[key] = updatedRow[key].slice(0, -2);
          }
        }
      });

      return updatedRow;
    });
  };

  const getMissingRows = (
    missing_records_details: any,
    resource_prefixes?: any,
  ) => {
    const recordData = missing_records_details[0]?.record_snapshot?.record_data;
    const rows: any = missing_records_details?.map((item: any, idx: any) => {
      return {
        id: item?.id,
        rowId: item?.id,
        assigned_user: item?.assigned_user,
        current_status: item?.current_status,
        comment: item?.comment,
        ...(recordData
          ? { ...item?.record_snapshot?.record_data }
          : { ...item?.comparison_keys }),
        ...getFormattedKeysPairs(
          item?.missing_in,
          item?.false_positive_missings,
          resource_prefixes,
          "missing",
        ),
        message: item?.error_message,
        ...item?.file_names,
      };
    });
    const processedRows = processRows(rows);
    setMissingRowsData(processedRows);
  };

  const handleIncidentResources = (id: number) => {
    const prefix = dashboardData?.additional_properties?.resource_prefixes;
    const rowData = missingResultsRuleDashboardDataSnapshot?.items.find(
      (item: any) => item.id === id,
    )?.record_snapshot?.record_data;

    const result = processIssueAnalysisData(rowData, prefix);

    return result;
  };

  // Enhanced grouping function to create distinct sections aligned with column grouping model
  const groupColumnsByPrefix = (columns: any[]) => {
    const prefixes =
      dashboardData?.additional_properties?.resource_prefixes || [];
    const resourceColumns: any[] = [];
    const prefixColumns: any[] = [];
    const commonColumns: any[] = [];

    // 1. First, collect base resource columns (exact matches with prefixes)
    prefixes.forEach((prefix: string) => {
      const baseColumn = columns.find((col) => col.field === prefix);
      if (baseColumn) {
        resourceColumns.push(baseColumn);
      }
    });

    // 2. Then, collect prefix-specific columns in organized order
    prefixes.forEach((prefix: string) => {
      // File name column (e.g., "Factset_file_name")
      const fileColumn = columns.find(
        (col) => col.field === `${prefix}_file_name`,
      );
      if (fileColumn) {
        prefixColumns.push(fileColumn);
      }

      // Other prefix-specific columns (excluding base, file_name, and Mismatched_)
      const otherPrefixColumns = columns.filter(
        (col) =>
          col.field.startsWith(`${prefix}_`) &&
          !col.field.endsWith("_file_name") &&
          !col.field.startsWith(`Mismatched_${prefix}_`),
      );
      prefixColumns.push(...otherPrefixColumns);

      // Mismatched value column (e.g., "Mismatched_Factset_Value")
      const mismatchedColumn = columns.find(
        (col) => col.field === `Mismatched_${prefix}_Value`,
      );
      if (mismatchedColumn) {
        prefixColumns.push(mismatchedColumn);
      }
    });

    // 3. Finally, collect columns that don't match any prefix pattern (common columns)
    columns.forEach((col) => {
      const isGrouped = prefixes.some(
        (prefix: string) =>
          col.field === prefix ||
          col.field.startsWith(`${prefix}_`) ||
          col.field === `Mismatched_${prefix}_Value`,
      );
      if (!isGrouped) {
        commonColumns.push(col);
      }
    });

    // Return in the order: Resources, Prefix-specific, Common
    return [...resourceColumns, ...prefixColumns, ...commonColumns];
  };

  const getMissingColumns = (
    missing_records_details: any,
    resource_prefixes?: any,
  ) => {
    let finalColumns;
    const recordData = missing_records_details[0]?.record_snapshot?.record_data;
    const resourceKeys = resource_prefixes ? resource_prefixes : [];

    // Get field-to-prefix mapping for header transformation
    const fieldToPrefixMap = getFieldToPrefixMappingForSnapshot(
      missing_records_details,
      dashboardData,
      "missing",
    );
    const resource_prifix_columns: any = resourceKeys.map((item: any) => {
      return {
        field: item,
        headerName: item,
        headerAlign: "center",
        align: "center",
        flex: 1,
        minWidth: 120,
        renderCell: (params: any) => {
          if (params.row[item] === "truepositive") {
            return <IconCheckCircleIconGreenSvg width={21} height={21} />;
          }
          if (params.row[item] === "secondaryfulfilled") {
            const secondaryResourceName = getSecondaryMergeResourceName(
              params.row[SECONDARY_MERGE_RESOURCES_KEY],
              item,
            );
            return (
              <Tooltip
                title={
                  <Typography sx={{ fontSize: "0.875rem" }}>
                    {`Fulfilled by secondary resource : ${secondaryResourceName}`}
                  </Typography>
                }
                placement="top"
                arrow
              >
                <span>
                  <IconSecondaryResourceSvg width={21} height={21} />
                </span>
              </Tooltip>
            );
          }
          if (params.row[item] === "falsepositive") {
            return (
              <>
                <IconFalsePositive />
              </>
            );
          }
          if (params.row[item] === "falsenegative") {
            return <IconCrossCircleIconSvg width={21} height={21} />;
          }
        },
      };
    });
    const message: any = {
      field: "message",
      headerName: "Message",
      flex: 1,
      minWidth: 350,
      headerAlign: "center",
      renderCell: (params: any) => {
        const regex = /;(?![\w\s])/g;
        return (
          <span className="text-ellipsis">
            <Tooltip
              title={
                <Typography sx={{ fontSize: "0.875rem" }}>
                  {params.value.replace(regex, "")}
                </Typography>
              }
              placement="top"
              className="tooltip-ellipsis"
            >
              {params.value.replace(regex, "")}
            </Tooltip>
          </span>
        );
      },
    };
    const columns: any = Object.keys(
      missing_records_details[0]?.comparison_keys,
    ).map((item: any) => {
      const originalHeaderName = getSanatizedCode(item);
      const transformedHeaderName = transformHeaderName(
        item,
        originalHeaderName,
        fieldToPrefixMap,
      );
      return {
        field: item,
        headerName: transformedHeaderName.replace(/^Key_/, ""),
        flex: 1,
        minWidth: 100,
        headerAlign: "center",
        align: "center",
        renderCell: (params: any) => {
          const fileNames = Object.keys(params.row)
            .filter((key) => key.includes("_file_name"))
            .map((key) => params.row[key])
            .filter((fileName) => fileName);

          const fileNamesDisplay =
            fileNames.length > 0 ? (
              <div>
                <strong>File name(s):</strong>
                <br />
                {fileNames.map((fileName, index) => {
                  const tempElement = document.createElement("div");
                  tempElement.innerHTML = fileName;
                  const fileNamesText =
                    tempElement.textContent || tempElement.innerText;
                  const individualFileNames =
                    fileNamesText.split(/\.xlsx|\.xls/);
                  return individualFileNames.map((fileName, subIndex) => {
                    const trimmedFileName = fileName.trim();
                    if (trimmedFileName) {
                      return (
                        <li key={`${index}-${subIndex}`}>
                          {trimmedFileName}.xlsx
                        </li>
                      );
                    }
                    return null;
                  });
                })}
              </div>
            ) : null;
          return (
            <span className="text-ellipsis">
              <Tooltip
                title={
                  <Typography sx={{ fontSize: "0.875rem" }}>
                    <ul className="pl-20">{fileNamesDisplay}</ul>
                  </Typography>
                }
                placement="top"
                className="tooltip-ellipsis"
              >
                {params.value}
              </Tooltip>
            </span>
          );
        },
      };
    });

    if (recordData) {
      const snapshotColumns = recordData
        ? Object.keys(recordData).map((key) => {
            const dynamicWidth = calculateDynamicWidthForSnapshot(
              key,
              missing_records_details,
              "missing",
            );
            const transformedHeaderName = transformHeaderName(
              key,
              key,
              fieldToPrefixMap,
            );
            return {
              field: key,
              headerName: transformedHeaderName.replace(/^Key_/, ""),
              flex: 1,
              minWidth: dynamicWidth > 120 ? dynamicWidth : 120,
            };
          })
        : [];
      const comparisonKeys = missing_records_details[0]?.comparison_keys;
      const excludedFields = ["Error_Message", "Missing_Resources_Prefixes"];
      const filtredSnapShot = snapshotColumns.filter(
        (column) =>
          !Object.keys(comparisonKeys).includes(column.field) &&
          !excludedFields.includes(column.field),
      );

      // For missing records, resource_prifix_columns ARE the base resource columns
      // So we need to separate them from other columns for proper grouping
      const dataColumns = [...filtredSnapShot]; // Only snapshot columns for prefix grouping
      const groupedDataColumns = groupColumnsByPrefix(dataColumns);

      finalColumns = [
        ...columns, // 1. Key columns (dynamic count, typically 2)
        message, // 3. Message column (now positioned immediately after resources)
        ...resource_prifix_columns, // 2. Resource columns (dynamic count based on prefixes)
        ...groupedDataColumns, // 4. Prefix-specific grouped columns
        // ...resColumns, // 5. Action columns (status, user, comment, action)
      ];
    } else {
      finalColumns = [
        ...columns, // 1. Key columns
        message, // 3. Message column (positioned right after resources)
        ...resource_prifix_columns, // 2. Resource columns (Resources group)
        // ...resColumns, // 4. Action columns
      ];
    }
    // Ensure no duplicate columns exist based on the "field" property
    const deduplicatedColumns = deduplicateByFieldProperty(
      finalColumns,
      "field",
    );
    setMissingColumnsData(excludeInternalResultColumns(deduplicatedColumns));
  };

  // useEffect hooks for data processing
  useEffect(() => {
    if (
      missingResultsRuleDashboardDataSnapshot?.items &&
      missingResultsRuleDashboardDataSnapshot?.items.length > 0
    ) {
      const keyColumns = Object.keys(
        missingResultsRuleDashboardDataSnapshot?.items[0]?.comparison_keys,
      ).map((key, value) => key);

      setKeyColumns(keyColumns);
      setIsLoading(false);
    }
  }, [missingResultsRuleDashboardDataSnapshot]);

  useEffect(() => {
    if (!missingResultsRuleDashboardDataSnapshot || !dashboardData) {
      return;
    }

    const columnGrouping = createColumnGroupingModelForSnapshot(
      dashboardData,
      missingResultsRuleDashboardDataSnapshot.items,
      "missing" as RecordType,
      false, // isAllColumnPinned - we'll handle this in child component
    );

    setColumnGroupingModel(columnGrouping);
  }, [dashboardData, missingResultsRuleDashboardDataSnapshot]);

  useEffect(() => {
    // missing records
    if (
      missingResultsRuleDashboardDataSnapshot?.items?.length > 0 &&
      dashboardData?.additional_properties?.resource_prefixes
    ) {
      getMissingColumns(
        missingResultsRuleDashboardDataSnapshot?.items,
        dashboardData?.additional_properties?.resource_prefixes,
      );
      getMissingRows(
        missingResultsRuleDashboardDataSnapshot?.items,
        dashboardData?.additional_properties?.resource_prefixes,
      );
    }
  }, [missingResultsRuleDashboardDataSnapshot, dashboardData]);

  const handleChange = (event: React.SyntheticEvent, newValue: string) => {
    setSelectedTab(newValue);
    setMismatchedColumns([]);
    setMissingColumns([]);
  };

  const tabContent: any = {
    missingRecords: (
      <>
        <div className="datatable-parent-container">
          {dashboardData?.is_issues_data_available ? (
            <MissingRecordsDashboardTab
              dashboardData={dashboardData}
              setMissingColumns={setMissingColumns}
              userDialogData={userDialogData}
              setUserDialogData={setUserDialogData}
            />
          ) : (
            <MissingSnapshotRecordsDashboardTab
              setMissingColumns={setMissingColumns}
              missingRowsData={missingRowsData}
              missingColumnsData={missingColumnsData}
              setMissingRowsData={setMissingRowsData}
              columnGroupingModel={columnGroupingModel}
              keyColumns={keyColumns}
              totalPages={missingResultsRuleDashboardDataSnapshot?.total}
              page={page}
              setPage={setPage}
              pSize={pSize}
              setPSize={setPSize}
              isLoading={isSnapshotLoading}
              dashboardData={dashboardData}
            />
          )}
        </div>
        {dashboardData?.comparison_research_query_results &&
          dashboardData?.comparison_research_query_results !== null && (
            <Box sx={{ mt: 2 }}>
              <RootCauseAnalysis
                selectedColumns={missingColumns}
                isLoading={isLoading}
                heading={"Research"}
                dashboardData={dashboardData}
              />
            </Box>
          )}
      </>
    ),
    mismatched: (
      <>
        {dashboardData?.is_issues_data_available ? (
          <MismatchedRecordsDashboardTab
            dashboardData={dashboardData}
            setMismatchedColumns={setMismatchedColumns}
            setUserDialogData={setUserDialogData}
            userDialogData={userDialogData}
          />
        ) : (
          <MismatchedSnapshotRecordsDashboardTab
            dashboardData={dashboardData}
            setMismatchedColumns={setMismatchedColumns}
            setUserDialogData={setUserDialogData}
            userDialogData={userDialogData}
          />
        )}
        {dashboardData?.comparison_research_query_results &&
          dashboardData?.comparison_research_query_results !== null && (
            <Box sx={{ mt: 2 }}>
              <RootCauseAnalysis
                selectedColumns={mismatchedColumns}
                isLoading={isLoading}
                heading={"Research"}
                dashboardData={dashboardData}
              />
            </Box>
          )}
      </>
    ),

    adhocQuery: (
      <Box className="adhock-query-accordion-wrapper">
        <AdhocQueryDashboardTab dashboardData={dashboardData} />
      </Box>
    ),
  };
  const missingCount =
    dashboardData?.additional_properties?.total_unique_missing_records ?? null;
  const mismatchedCount =
    dashboardData?.additional_properties?.total_mismatched_records ?? null;
  const adhocCount = resolveAdhocQueryRecordCount({
    adhocQueries: dashboardData?.additional_properties?.adhoc_queries_result,
    isDetailedExecutionDataAvailable:
      dashboardData?.is_detailed_execution_data_available,
    summaryTotal:
      dashboardData?.additional_properties?.total_adhoc_queries_result_records,
  });

  const TAB_COLORS: Record<string, string> = {
    missingRecords: "#d32f2f",
    mismatched: "#e65100",
    adhocQuery: "#00796b",
  };

  const activeColor = TAB_COLORS[selectedTab] ?? "#1976d2";

  const tabLabel = (label: string, count: number | null, tabKey: string) => (
    <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
      {label}
      {count !== null && count > 0 && (
        <Chip
          label={count}
          size="small"
          sx={{
            height: 18,
            fontSize: "0.7rem",
            fontWeight: 700,
            backgroundColor: TAB_COLORS[tabKey],
            color: "#fff",
            "& .MuiChip-label": { px: 0.75 },
          }}
        />
      )}
    </Box>
  );

  return (
    <Accordion defaultExpanded className="heading-bold">
      <AccordionSummary
        aria-controls="issue-details-content"
        id="issue-details-header"
        expandIcon={<GridExpandMoreIcon />}
      >
        <h4>Issue Details</h4>
      </AccordionSummary>
      <AccordionDetails sx={{ paddingTop: "16px" }}>
        <Grid container className="tabs-with-butttons">
          <Grid item xs>
            <Tabs
              value={selectedTab}
              onChange={handleChange}
              className="mui-tabs no-t-lr-radius min-height-0 alternative-1"
              TabIndicatorProps={{
                style: { backgroundColor: activeColor },
              }}
              sx={{
                "& .MuiTab-root.Mui-selected": { color: activeColor },
              }}
            >
              <Tab
                value="missingRecords"
                label={tabLabel(
                  "Missing Records",
                  missingCount,
                  "missingRecords",
                )}
              />
              <Tab
                value="mismatched"
                label={tabLabel(
                  "Mismatched Rows",
                  mismatchedCount,
                  "mismatched",
                )}
              />
              <Tab
                value="adhocQuery"
                label={tabLabel("Adhoc Query", adhocCount, "adhocQuery")}
              />
            </Tabs>
          </Grid>
          {selectedTab === "missingRecords" || selectedTab === "mismatched" ? (
            <Grid item className="buttons-group">
              <Button className="btn-blue btn-orange btn-sm">
                <IconBoltType /> Action
              </Button>
              {canEditRuleExecutionIssues &&
                dashboardData?.is_issues_data_available && (
                  <Button
                    className="btn-blue btn-orange btn-sm"
                    onClick={() => {
                      setUserDialogData((prev: any) => ({
                        ...prev,
                        status: true,
                        dialogName: "Update Issue",
                      }));
                    }}
                    disabled={userDialogData?.selectedIds?.length === 0}
                  >
                    Update Issue
                  </Button>
                )}

              <Button
                className="btn-blue btn-orange btn-sm"
                onClick={() => {
                  navigate("/incident-reporting-rerun-report");
                }}
              >
                Historical Trail
              </Button>
            </Grid>
          ) : null}
        </Grid>

        <Box>{tabContent[selectedTab]}</Box>
      </AccordionDetails>
    </Accordion>
  );
};

export default ComparisonDashboardResults;
